import { PageViewTracker } from '@belong/common';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { ReviewsPage } from 'src/features/reviews/components/reviews-page/reviews-page';
import { reviewsLayout } from 'src/features/reviews/components/reviews-page/reviews-layout';
import { ReviewsSeo } from 'src/features/reviews/components/reviews-page/reviews-seo';
import {
  getReviewFeedPage,
  getReviewPageCount,
} from 'src/features/reviews/reviews.queries';
import {
  REVALIDATE_SECONDS,
  parsePageParam,
} from 'src/features/reviews/reviews.static';
import {
  REVIEWS_URLS,
  reviewsPageHref,
} from 'src/features/reviews/reviews.urls';
import type { ReviewFeedPage } from 'src/features/reviews/reviews.utils';

type ReviewsPageProps = {
  feedPage: ReviewFeedPage;
  totalPages: number;
};

/**
 * `/reviews/page/2` and on: the rest of the feed, one page per URL.
 *
 * This route exists for readers who do not run JavaScript, and every search
 * engine and LLM is one of them some of the time. The feed's "view more"
 * button appends in the browser, which means that without these pages the only
 * reviews anything could read were the first forty-five of fifteen hundred —
 * on a page whose whole argument is that all of them are here.
 *
 * It renders the same page as `/reviews`, not a stripped archive of it: a
 * reader landing on page seven from a search result should get the page
 * everyone else got.
 */
export default function ReviewsPageRoute({
  feedPage,
  totalPages,
}: ReviewsPageProps) {
  return (
    <PageViewTracker name="reviews_page">
      <ReviewsSeo feedPage={feedPage} totalPages={totalPages} />
      <ReviewsPage feedPage={feedPage} />
    </PageViewTracker>
  );
}

/**
 * Every page after the first, built ahead of time.
 *
 * `blocking` rather than `false`, because the feed grows: the thirty-sixth
 * page will exist before the next deploy does, and a reader who follows a
 * `next` link to it should get the page rather than a 404. A page past the end
 * still 404s — `getStaticProps` decides that, not this list.
 *
 * Page one is deliberately absent: `/reviews/page/1` would be the same
 * forty-five reviews at a second URL, so it redirects instead.
 */
export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const totalPages = await getReviewPageCount();

    return {
      paths: Array.from(
        { length: Math.max(0, totalPages - 1) },
        (_, index) => ({ params: { page: String(index + 2) } }),
      ),
      fallback: 'blocking',
    };
  } catch (error) {
    // The warehouse was unreachable at build time. `/reviews` itself survives
    // that by shipping its empty state; this route survives it by building
    // nothing up front and letting `blocking` build each page on its first
    // request, once the warehouse is back. A throw here would fail the build
    // of the whole site over one section's data source.
    console.error('[reviews] could not count the feed pages at build', error);

    return { paths: [], fallback: 'blocking' };
  }
};

export const getStaticProps: GetStaticProps<ReviewsPageProps> = async ({
  params,
}) => {
  const page = parsePageParam(params?.page);

  if (page === undefined) {
    return { notFound: true };
  }

  if (page === 1) {
    // The same reviews as `/reviews`. Sending the reader there rather than
    // publishing a duplicate is the whole reason `getStaticPaths` starts at
    // two; a canonical tag would leave the duplicate up and ask a crawler to
    // ignore it.
    return {
      redirect: { destination: REVIEWS_URLS.REVIEWS, permanent: true },
    };
  }

  const [feedPage, totalPages] = await Promise.all([
    getReviewFeedPage(page),
    getReviewPageCount(),
  ]);

  // Past the end of the feed. Not an empty page: a URL that will never have
  // anything on it should say so, or it sits in an index as a thin page for as
  // long as it is crawled.
  if (feedPage.reviews.length === 0) {
    return { notFound: true, revalidate: REVALIDATE_SECONDS };
  }

  return { props: { feedPage, totalPages }, revalidate: REVALIDATE_SECONDS };
};

ReviewsPageRoute.getLayout = reviewsLayout;

/** Exported for the sitemap, which lists the same URLs this route serves. */
export { reviewsPageHref };
